import { Overlay, OverlayRef, OverlaySizeConfig, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, OnDestroy } from '@angular/core';
import { MatIntroComponent } from './mat-intro.component';
import { Observable, Subject, Subscription, finalize } from 'rxjs';
import { ApiService } from '@lib/services/api.service';

export type MatIntroStep = {
    delay?: number
    message: string
    position?: string
    canInteract?: boolean
    element?: () => HTMLElement
    next?: () => HTMLElement
    skip?: () => boolean
}

export type MatIntroStepExtend = MatIntroStep & {
    index: number,
    total: number
}

export type MatIntroOptions = {
    name: string
    title: string
    description?: string
    start?: string
    canAcces?: string
    steps: MatIntroStep[]
}

export type HTMLElementWithCheckVisibility = HTMLElement & {
    checkVisibility?: Function
}

@Injectable({
  providedIn: 'root'
})
export class MatIntroService implements OnDestroy {

    private subscriptions: Subscription = new Subscription();

    private index: number;
    private options: MatIntroOptions;
    private overlayRef: OverlayRef;
    private overlayComponentRef: ComponentRef<MatIntroComponent>;

    private elementMutationsObserver: MutationObserver;
    
    elementWaitingSubscription: Subscription;

    private _onStateChange: Subject<{name: string, index: number, length?: number}> = new Subject();

    onStateChange: Observable<{name: string, index: number, length?: number}> = this._onStateChange.asObservable();

    constructor(private overlay: Overlay,
                private apiService: ApiService) {}

    getState(name: string): Observable<{name: string, index: number}> {
        return this.apiService.get(`guides/${name}`);
    }

    need (name: string, load: () => Promise<MatIntroOptions>): () => void {

        // this.getState(name).subscribe(state => {

        //     if(isNaN(state.index))
        //         load().then(guide => this.play(guide));
        // });

        return () => this.replay(name, load);
    }

    replay (name: string, load: () => Promise<MatIntroOptions>) {

        this.reset(name).subscribe(() => load().then(guide => this.play(guide)));
    }

    play (options: MatIntroOptions) {

        this.options = options;
        this.subscriptions = new Subscription();
        this.next(0);
    }

    next (index?: number) {

        if(this.elementMutationsObserver)
            this.elementMutationsObserver.disconnect();

        if(index !== undefined)
            this.index = index;
        else if(this.index < this.options.steps.length - 1)
            this.index++;

        const step: MatIntroStep = this.options.steps[this.index];

        if(step.skip instanceof Function && step.skip() === true)
            this.next();
        else
            this.elementWaitingSubscription = this.waitingForElement(step.element, step.delay).subscribe(element => {

                if(this.checkAllreadyNext())
                    this.next();
                else {
                    this.checkNext();
                    this.saveState();
                    this.updateOverlay(element);
                    this.checkMutations(element);
                }
            });
    }

    end () {
        this.ngOnDestroy();
        this.saveState();
    }

    reset (name: string): Observable<void> {
        return this.apiService.delete(`guides/${name}`);
    }

    private saveState () {
        this.apiService.post(`guides/${this.options.name}`, {index: this.index}).subscribe(state => {
            this._onStateChange.next({...state, length: this.options.steps.length});
        });
    }

    checkAllreadyNext (): boolean {

        const step: MatIntroStep = this.options.steps[this.index];
        const hasElement: boolean = step.next instanceof Function;  

        if(hasElement) {
            let element: HTMLElementWithCheckVisibility = step.next();
            return element && element?.checkVisibility({checkVisibilityCSS: true});
        } else
            return false;
    }

    checkNext () {

        const step: MatIntroStep = this.options.steps[this.index];

        this.subscriptions.add(
            this.waitingForElement(step.next).subscribe(element => {
    
                if(element) this.next();
            })
        );
    }

    checkMutations (element?: HTMLElement) {
        
        if(element) {
            this.elementMutationsObserver = new MutationObserver(() => this.updateOverlay(element));
            this.elementMutationsObserver.observe(element, {childList: true, subtree: true, attributes: true});
        }
    }

    updateOverlay (element?: HTMLElementWithCheckVisibility) {
        
        const step: MatIntroStep = this.options.steps[this.index];
        const {top, left, width, height} = (element && element?.checkVisibility()) ? 
            element.getBoundingClientRect() : 
            {top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0};

        const size: OverlaySizeConfig = {width, height};
        const positionStrategy: PositionStrategy = this.overlay.position().global().top(`${top}px`).left(`${left}px`);
        const scrollStrategy = this.overlay.scrollStrategies.noop();

        if(!this.overlayRef || !this.overlayRef.hasAttached()) {
            this.overlayRef = this.overlay.create({
                scrollStrategy,
                positionStrategy, 
                hasBackdrop: false,
                disposeOnNavigation: false,
                backdropClass: 'cdk-overlay-transparent-backdrop',
                panelClass: 'cdk-overlay-pane-pointer-events-none'
            });

            this.overlayComponentRef = this.overlayRef.attach(new ComponentPortal(MatIntroComponent));
            
        } else {
            
            this.overlayRef.addPanelClass('cdk-overlay-pane-position-animate');
            this.overlayRef.updatePositionStrategy(positionStrategy);
        }

        this.overlayRef.hostElement.style.zIndex = '1001';
        this.overlayRef.updateSize(size);

        this.overlayComponentRef.instance.step = {...step, index: this.index, total: this.options.steps.length};
        this.overlayComponentRef.instance.updatePosition({top, left, width, height});
    }

    private waitingForElement (elementSelectorFn?: () => HTMLElement, delay: number = 0): Observable<HTMLElement> {

        let waitElementObserver: MutationObserver;

        return new Observable<HTMLElement>(observer => {

            const hasElement: boolean = elementSelectorFn instanceof Function;
            const complete = (element?: HTMLElement) => setTimeout(() => {observer.next(element); observer.complete();}, delay);

            if(hasElement) {
                let element: HTMLElementWithCheckVisibility = elementSelectorFn();

                if(element && element?.checkVisibility({checkVisibilityCSS: true}))
                    this.ensureIsInViewport(element).subscribe(complete);
                else {

                    waitElementObserver = new MutationObserver(() => {
                        element = elementSelectorFn();

                        if(element && element?.checkVisibility({checkVisibilityCSS: true})) {

                            waitElementObserver.disconnect();
                            this.ensureIsInViewport(element).subscribe(complete);
                        }
                    });

                    waitElementObserver.observe(document.body, {childList: true, subtree: true});
                }
            } else complete();
        }).pipe(
            finalize(() => {

                if(waitElementObserver)
                    waitElementObserver.disconnect();
            })
        );
    }

    private ensureIsInViewport(element?: HTMLElement): Observable<HTMLElement> {
        return new Observable<HTMLElement>(observer => {

            const complete = (element?: HTMLElement) => {observer.next(element); observer.complete();};

            if(element) {

                const { top, bottom } = element.getBoundingClientRect();

                if(top > 0 && bottom < (window.innerHeight || document.documentElement.clientHeight))
                    complete(element);
                else {
                    
                    addEventListener('scrollend', () => complete(element), {capture: true, once: true});
                    element.scrollIntoView({behavior: "smooth", block: "center", inline: "center"});
                }
            } else 
                complete(element);
        });
    }

    ngOnDestroy(): void {

        if(this.elementMutationsObserver)
            this.elementMutationsObserver.disconnect();
        
        if(this.elementWaitingSubscription)
            this.elementWaitingSubscription.unsubscribe();

        if(this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
            this.overlayComponentRef.destroy();
        }

        this.subscriptions.unsubscribe();
    }
}
